(ns frontend.ux-overhaul.listing.pipeline-entity.default-views
  "The default views of the four pipeline listings: deals, sales, fundraising and
   exit. The legacy grids keep these views in code, and so does the new listing.
   A default view is not an entity in the database, so a user cannot rename it,
   delete it or change it.

   A default view sets the view mode, the filters and, for a view of the grid,
   the sort. The filters have the shape that the filter bar makes, so that the
   filter bar shows them as its own and both the grid and the Kanban board use
   them. The sort is the sort of the grid, as in the legacy grids, and it comes
   with its column shown. The legacy columns of each view are not part of the
   default view. The legacy board views sort the cards, and this is a separate
   setting that the default view does not change."
  (:require
   [common.dates :as dates]
   [common.entity-name :as entity-name]
   [common.time :as c-time]))

(def ^:private definitions
  {:entity.type/oppo
   {:status-field :oppo/status
    :owner-field  :oppo/owner
    :views        [{:id        :active-pipeline
                    :name-key  :active-pipeline
                    :view-mode :kanban
                    :status    :oppo.status/ok}
                   {:id        :all
                    :name-key  :all-deals
                    :view-mode :grid
                    :sort      {:field :oppo/name :direction :asc}}
                   {:id        :mine
                    :name-key  :my-deals
                    :view-mode :grid
                    :sort      {:field :common/updated-on :direction :desc}
                    :mine?     true}
                   {:id        :passed
                    :name-key  :passed-cases
                    :view-mode :grid
                    :sort      {:field :oppo/passed-on :direction :desc}
                    :status    :oppo.status/passed}
                   {:id        :on-hold
                    :name-key  :deals-on-hold
                    :view-mode :grid
                    :sort      {:field :oppo/put-on-hold-on :direction :desc}
                    :status    :oppo.status/on-hold}
                   {:id                :new-this-quarter
                    :name-key          :new-deals-this-q
                    :view-mode         :grid
                    :sort              {:field :common/created-on :direction :desc}
                    :new-this-quarter? true}]}

   :entity.type/sales-opportunity
   {:status-field :sales-opportunity/status
    :owner-field  :sales-opportunity/owner
    :views        [{:id        :active-pipeline
                    :name-key  :active-pipeline
                    :view-mode :kanban
                    :status    :sales-opportunity.status/active}
                   {:id        :all
                    :name-key  :all-sales-opportunities
                    :view-mode :grid
                    :sort      {:field :sales-opportunity/name :direction :asc}}
                   {:id        :mine
                    :name-key  :my-sales-opportunities
                    :view-mode :grid
                    :sort      {:field :sales-opportunity/name :direction :asc}
                    :mine?     true}
                   {:id        :lost
                    :name-key  :lost-sales-opportunities
                    :view-mode :grid
                    :sort      {:field :sales-opportunity/lost-on :direction :desc}
                    :status    :sales-opportunity.status/lost}
                   {:id        :on-hold
                    :name-key  :sales-opportunities-on-hold
                    :view-mode :grid
                    :sort      {:field :sales-opportunity/put-on-hold-on :direction :desc}
                    :status    :sales-opportunity.status/on-hold}]}

   :entity.type/fundraising-opportunity
   {:status-field :fundraising-opportunity/status
    :owner-field  :fundraising-opportunity/owner
    :views        [{:id        :active-pipeline
                    :name-key  :active-pipeline
                    :view-mode :kanban
                    :status    :fundraising-opportunity.status/active}
                   {:id        :all
                    :name-key  :all-fundraising-opportunities
                    :view-mode :grid}
                   {:id        :mine
                    :name-key  :my-fundraising-opportunities
                    :view-mode :grid
                    :mine?     true}
                   {:id        :lost
                    :name-key  :lost-fundraising-opportunities
                    :view-mode :grid
                    :sort      {:field :fundraising-opportunity/lost-on :direction :desc}
                    :status    :fundraising-opportunity.status/lost}
                   {:id        :on-hold
                    :name-key  :fundraising-opportunities-on-hold
                    :view-mode :grid
                    :sort      {:field :fundraising-opportunity/put-on-hold-on :direction :desc}
                    :status    :fundraising-opportunity.status/on-hold}
                   {:id                :new-this-quarter
                    :name-key          :new-fundraising-opportunities-this-q
                    :view-mode         :grid
                    :sort              {:field :common/created-on :direction :desc}
                    :new-this-quarter? true}]}

   :entity.type/exit-opportunity
   {:status-field :exit-opportunity/status
    :owner-field  :exit-opportunity/owner
    :views        [{:id        :active-pipeline
                    :name-key  :active-pipeline
                    :view-mode :kanban
                    :status    :exit-opportunity.status/active}
                   {:id        :all
                    :name-key  :all-exit-opportunities
                    :view-mode :grid
                    :sort      {:field :exit-opportunity/name :direction :asc}}
                   {:id        :mine
                    :name-key  :my-exit-opportunities
                    :view-mode :grid
                    :sort      {:field :exit-opportunity/name :direction :asc}
                    :mine?     true}
                   {:id        :lost
                    :name-key  :lost-exit-opportunities
                    :view-mode :grid
                    :sort      {:field :exit-opportunity/lost-on :direction :desc}
                    :status    :exit-opportunity.status/lost}
                   {:id        :on-hold
                    :name-key  :exit-opportunities-on-hold
                    :view-mode :grid
                    :sort      {:field :exit-opportunity/put-on-hold-on :direction :desc}
                    :status    :exit-opportunity.status/on-hold}]}})

(defn- option-selection
  "A selection as the filter bar makes it for a chosen option. The id is the text
   of the selection, as in the filter bar, so that a choice of the same option in
   the filter bar replaces this selection and does not add a second one."
  [{:keys [operator value label]}]
  (let [selection {:kind     :value
                   :operator operator
                   :value    value
                   :label    label}]
    (assoc selection :id (pr-str selection))))

(defn- field-filter
  [{:keys [field mode selection]}]
  [[field]
   {:field-path [field]
    :mode       mode
    :selections [selection]}])

(defn- keyword->option-value
  "The value that the filter bar has for a keyword option. The facets give
   `\"oppo.status/ok\"` for `:oppo.status/ok`."
  [kw]
  (str (namespace kw) "/" (name kw)))

(defn quarter-start-millis
  "The start of the quarter of `now-millis`, as a date of the filter bar. The date
   filters of the listing are dates at midnight UTC."
  [now-millis]
  (c-time/start-of-q-millis (dates/date (js/Date. now-millis))))

(defn- view-filters
  [{:keys [status-field owner-field]}
   {:keys [status mine? new-this-quarter?]}
   {:keys [account now-millis tr]}]
  (into {}
        (cond-> []
          status
          (conj (field-filter
                 {:field     status-field
                  :mode      :multi
                  :selection (option-selection {:operator :operand/any-of
                                                :value    (keyword->option-value status)
                                                :label    (tr status)})}))

          mine?
          (conj (field-filter
                 {:field     owner-field
                  :mode      :multi
                  :selection (option-selection {:operator :operand/any-of
                                                :value    (:db/id account)
                                                :label    (entity-name/entity-name nil tr (assoc account :entity/type :entity.type/account))})}))

          new-this-quarter?
          (conj (field-filter
                 {:field     :common/created-on
                  :mode      :single
                  :selection (let [start (quarter-start-millis now-millis)]
                               (option-selection {:operator :operand/on-or-after
                                                  :value    start
                                                  :label    (str start)}))})))))

(defn default-views
  "The default views of the listing of `entity-type`, in the order that they are
   shown. Each has the keys of a named view that the saved view code reads:
   `:db/id` and `:named-view/settings`. The name of a default view is a
   localization key, `:name-key`, and not a string, so that it follows the
   language of the user.

   `account` is the account of the user. `now-millis` gives the start of the
   quarter. `tr` translates the labels of the filters. An entity type that has
   no default views gives nil."
  [{:keys [entity-type] :as args}]
  (when-let [definition (get definitions entity-type)]
    (mapv (fn [{:keys [id name-key view-mode sort] :as view}]
            {:db/id               (str "default-view/" (name entity-type) "/" (name id))
             :default-view?       true
             :name-key            name-key
             :named-view/settings (cond-> {:view-mode view-mode
                                           :filters   (view-filters definition view args)}
                                    sort
                                    (assoc :grid {:sorted-by [{:field-path [(:field sort)]
                                                               :direction  (:direction sort)}]}))})
          (:views definition))))
